// Top-level entry point for the real Calculation tab content - replaces
// CalculationSummaryPanel's usage in CalculationTab.js. Wires
// CategorySidebar + GroupPanel together over `calculationResult.calculations`
// and provides the "Edit Hard-Coded Values" action that opens
// HardcodedValuesDialog.
//
// Props:
//  - reportingId (string, required): passed through to HardcodedValuesDialog
//    for its POST .../calculate call after saving settings.
//  - calculationResult (object|null): the GET
//    /compartment-report/reportings/:id/calculation response (see
//    calculations.py::run_calculations()) - same shape CalculationTab.js
//    already fetches into its `calculationResult` state today.
//  - setLoading (function): page-level loading toggle, threaded down to
//    HardcodedValuesDialog so its PUT settings + POST calculate calls show
//    the same loading affordance CalculationTab.js's own Calculate/Generate
//    Reporting buttons already use.
//  - onRecalculated (function, optional): NOT in the brief's original 3-prop
//    list, but needed for correct behavior - called after "Confirm &
//    Re-Calculate" successfully saves settings and re-runs Calculate, so the
//    parent can refetch `calculationResult` (CalculationTab.js's own
//    `fetchResults`/`onStageChanged` are private to that component; wire
//    `onRecalculated={fetchResults}` - or `onRecalculated={() => {
//    fetchResults(); onStageChanged(); }}` if you also want the stage-status
//    refresh - when importing this component). If left unset, the dialog
//    still saves + recalculates correctly, it just won't refresh what's
//    shown here until the user navigates away and back.

import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import CategorySidebar from './CategorySidebar';
import GroupPanel from './GroupPanel';
import HardcodedValuesDialog from './HardcodedValuesDialog';
import { CALCULATION_CATEGORIES } from './calcFormat';

const CalculationResultsView = ({ reportingId, calculationResult, setLoading, onRecalculated }) => {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isDialogVisible, setIsDialogVisible] = useState(false);

  const calculations = (calculationResult && calculationResult.calculations) || [];

  const groupLabelById = new Map(calculations.map((e) => [e.group, e.group_label]));

  const categories = CALCULATION_CATEGORIES.map((cat) => ({
    id: cat.id,
    label: cat.label,
    groups: cat.groups.filter((g) => groupLabelById.has(g)),
  })).filter((cat) => cat.groups.length > 0);

  useEffect(() => {
    if (selectedGroup && groupLabelById.has(selectedGroup)) return;
    const firstGroup = (categories[0] && categories[0].groups[0]) || null;
    setSelectedGroup(firstGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculationResult]);

  const handleSaved = () => {
    onRecalculated && onRecalculated();
  };

  if (!calculationResult) {
    return <p className="cr-muted-text">No calculation result yet - run Calculate first.</p>;
  }

  return (
    <div>
      <div className="ex-section-header">
        <div>
          <h4 className="ex-section-title">Calculation Results</h4>
          <div className="ex-section-subtitle">Pick a group on the left; hover underlined values for their formula.</div>
        </div>
        <Button
          label="Edit Hard-Coded Values"
          icon="pi pi-sliders-h"
          className="p-button-secondary p-button-outlined p-button-sm"
          onClick={() => setIsDialogVisible(true)}
        />
      </div>

      <div className="cr-results-layout">
        <CategorySidebar
          categories={categories}
          groupLabelById={groupLabelById}
          selectedGroup={selectedGroup}
          onSelectGroup={setSelectedGroup}
        />
        <div className="cr-results-main">
          {selectedGroup ? (
            <GroupPanel
              reportingId={reportingId}
              groupId={selectedGroup}
              groupLabel={groupLabelById.get(selectedGroup) || selectedGroup}
              entries={calculations.filter((e) => e.group === selectedGroup)}
            />
          ) : (
            <p className="cr-muted-text">No calculation groups to show.</p>
          )}
        </div>
      </div>

      <HardcodedValuesDialog
        reportingId={reportingId}
        visible={isDialogVisible}
        onHide={() => setIsDialogVisible(false)}
        onSaved={handleSaved}
        setLoading={setLoading}
      />
    </div>
  );
};

export default CalculationResultsView;
